/** Static department tree for DepartmentPickerDialog (Storybook / demo). */
export const MOCK_DEPARTMENT_TREE = [
  {
    id: 1,
    name: "Division 1",
    code: "DIV1",
  },
  {
    id: 2,
    name: "Division 2",
    code: "DIV2",
    children: [{ id: 21, name: "Department 2A", code: "DEP2A" }],
  },
  {
    id: 3,
    name: "Division 3",
    code: "DIV3",
    children: [
      { id: 31, name: "Department 3", code: "DEP3" },
      { id: 32, name: "Department 3B", code: "DEP3B" },
    ],
  },
  {
    id: 4,
    name: "Division 4",
    code: "DIV4",
    children: [
      { id: 41, name: "Department 4", code: "DEP4" },
      { id: 42, name: "Department 4B", code: "DEP4B" },
    ],
  },
];

export const DEPARTMENT_SEARCH_DELAY_MS = 0;

const SKELETON_BAR_WIDTHS = ["62%", "38%", "78%", "55%", "44%"];

export function getSkeletonBarWidths() {
  return SKELETON_BAR_WIDTHS;
}

/**
 * Walk tree nodes depth-first; collect matching items with their full ancestor path.
 * Same depth-first walk pattern as mapApiDepartmentsToTableRows.
 */
function walkAndSearch(nodes, normalized, ancestorPath, results, ancestorMatched = false) {
  nodes.forEach((node) => {
    const n = (node.name || "").toLowerCase();
    const c = String(node.code || "").toLowerCase();
    const nodeMatches = n.includes(normalized) || c.includes(normalized);
    const shouldInclude = nodeMatches || ancestorMatched;

    if (shouldInclude) {
      results.push({ id: node.id, name: node.name, code: node.code, path: ancestorPath });
    }

    if (node.children && node.children.length) {
      walkAndSearch(
        node.children,
        normalized,
        [...ancestorPath, { id: node.id, name: node.name, code: node.code }],
        results,
        shouldInclude
      );
    }
  });
}

/**
 * Search departments by name or code; returns matches with full ancestor path (root-first).
 * An empty query returns every department (the dialog's default list).
 *
 * For real API data (flat list with parent_department_id): builds byId map + children links
 * exactly like mapApiDepartmentsToTableRows, then walks depth-first.
 *
 * For Storybook mock data (items already have children): walks the existing tree directly.
 *
 * @returns {Array<{ id, name, code, path: Array<{ id, name, code }> }>}
 */
export function searchMockDepartments(flatList, query) {
  const normalized = (query || "").trim().toLowerCase();
  if (!Array.isArray(flatList) || !flatList.length) return [];

  const isRawApiData = "parent_department_id" in flatList[0];

  let roots;

  if (isRawApiData) {
    // Same pattern as mapApiDepartmentsToTableRows
    const byId = new Map();
    flatList.forEach((d) => {
      byId.set(d.id, { id: d.id, name: d.name, code: d.code, children: [] });
    });

    roots = [];
    flatList.forEach((d) => {
      const parentId = d.parent_department_id ?? d.parent_id ?? d.parent ?? null;
      const node = byId.get(d.id);
      if (parentId != null && byId.has(parentId)) {
        byId.get(parentId).children.push(node);
      } else {
        roots.push(node);
      }
    });
  } else {
    roots = flatList;
  }

  const results = [];
  walkAndSearch(roots, normalized, [], results);
  return results;
}
